import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { AccountView, CompanyView, SaveAccountParameters, SearchCompaniesForAccountParameters } from '@api-proxies';
import { CompanyService } from '@http/clients/company.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { AwaiterService } from '@services/awaiter.service';
import { GridService } from '@services/grid.service';
import { IKebabAction } from '@shared/grids/custom-cell-actions/custom-cell-actions.component';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { TypeAheadCompaniesComponent } from '@shared/type-ahead-companies/type-ahead-companies.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { ACCOUNT_COMPANIES_GRID_COLUMNS } from './accounts-single-companies.config';

@Component({
    selector: 'app-accounts-single-companies',
    templateUrl: './accounts-single-companies.component.html',
    styleUrls: ['./accounts-single-companies.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadCompaniesComponent]
})
export class AccountsSingleCompaniesComponent {
    account = input<AccountView>();
    tabCounterChange = output<void>();
    companiesDatasource?: IServerSideDatasource;
    gridColumns: ColDef[] = [...ACCOUNT_COMPANIES_GRID_COLUMNS];
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    typeAheadSearchByEntityParameters: ITypeAheadDropdownEntityParameters = {
        entityKeyName: EntityTypes.Account
    };

    private readonly companyService = inject(CompanyService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly gridService = inject(GridService);

    constructor() {
        this.setupGridColumns();

        effect(() => {
            const account = this.account();
            const accountId = account?.id;
            const organizationId = account?.organizationId;

            if (!accountId) return;

            this.typeAheadSearchByEntityParameters = {
                entityKeyName: EntityTypes.Account,
                entity: {
                    accountId,
                    organizationId
                }
            };

            if (!this.companiesDatasource) {
                this.createDatasource();
                return;
            }

            this.refreshGridView();
        });
    }

    getRowIdFn = (params: any) => params.data?.id?.toString();

    async onSelectedCompany(company: CompanyView): Promise<void> {
        const accountId = this.account()?.id;
        if (!company || !accountId) return;

        try {
            const companySaved = await this.companyService.setAccount({
                id: company.id,
                accountId
            } as SaveAccountParameters);

            if (companySaved) {
                const gridApi = this.gridService.getGridApi();
                let applied = false;

                if (gridApi?.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        add: [companySaved]
                    });
                    applied = transactionResult?.status === 'Applied';
                }

                if (!applied) {
                    this.refreshGridView();
                }
            }
            this.noResults.set(false);
        } catch {
            this.snackbarNotificationService.error('An error occurred while trying to save the company');
        }
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    async removeItem(company: CompanyView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove company',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this company from the company list?',
            title: 'Remove company'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction(
            'Removing company',
            async () => {
                if (!company) return;

                await this.companyService.removeAccount(company.id);

                this.tabCounterChange.emit();

                const gridApi = this.gridService.getGridApi();
                let removed = false;

                if (gridApi?.applyServerSideTransaction) {
                    const node = gridApi.getRowNode(company.id.toString());
                    if (node?.data) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            remove: [node.data]
                        });
                        removed = transactionResult?.status === 'Applied';
                    }
                }

                if (removed && gridApi) {
                    this.noResults.set(gridApi.getDisplayedRowCount() === 0);
                } else {
                    this.refreshGridView();
                }
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Remove',
                action: (company: CompanyView) => this.removeItem(company)
            }
        ];

        this.gridColumns = ACCOUNT_COMPANIES_GRID_COLUMNS.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchCompaniesForAccountParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const accountId = this.account()?.id;
        if (!accountId) {
            throw new Error('Account is required to search companies for account.');
        }
        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            accountId
        } as SearchCompaniesForAccountParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.companiesDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.companyService.searchForAccount(searchParams);

                    const rowData = response?.results ?? [];
                    const totalCount = response?.pageInfo?.itemsCount ?? 0;

                    this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                    params.success({
                        rowData,
                        rowCount: totalCount
                    });
                } catch (error) {
                    console.error('Error fetching companies:', error);
                    params.fail();
                    this.noResults.set(true);
                    this.snackbarNotificationService.error('An error occurred while trying to load the companies');
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }
}
